import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import RenewalAddressCard from '../components/RenewalAddressCard';
import RenewalEquipmentCard, { type RenewalEquipmentData } from '../components/RenewalEquipmentCard';
import RenewalOfferSection, { type RenewalReviewOffer } from '../components/RenewalOfferSection';
import RenewalPersonalDataCard, { type RenewalPersonalData } from '../components/RenewalPersonalDataCard';
import { RENEWAL_APP_IDS } from '../renewalAppIds';
import type { RenewalAddressFields, RenewalPersonalFields } from '../renewalReview';

export type { RenewalReviewOffer };

export type RenewalReviewData = {
  personal: RenewalPersonalData;
  address: RenewalAddressFields;
  equipment: RenewalEquipmentData;
};

type Props = {
  data: RenewalReviewData;
  onSaveCustomer: (values: RenewalPersonalFields) => Promise<void>;
  onSaveAddress: (values: RenewalAddressFields) => Promise<void>;
  /** Coberturas, upsell, resumo e termos; sem cotação carregada, só os cards. */
  offer?: RenewalReviewOffer;
};

type EditingCard = 'personal' | 'address' | null;

/** Etapa «Confira seus dados» (Figma 2086:7579): três cards; só um em edição por vez. */
export default function RenewalReviewStep({ data, onSaveCustomer, onSaveAddress, offer }: Props) {
  const [editing, setEditing] = useState<EditingCard>(null);
  const close = () => setEditing(null);

  return (
    <Box
      data-app-id={RENEWAL_APP_IDS.stepReview}
      data-testid="renewal-step-review"
      sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}
    >
      <Box>
        <Typography component="h1" sx={{ color: 'text.primary' }} variant="h5">
          Confira seus dados para renovar o contrato
        </Typography>
        <Typography sx={(theme) => ({ color: theme.customColors.textPlaceholder })} variant="subtitle1">
          Se algo mudou, atualize seus dados pessoais ou o endereço antes de continuar.
        </Typography>
      </Box>

      <Box
        sx={{
          alignItems: 'stretch',
          display: 'grid',
          gap: '24px',
          gridTemplateColumns: { md: 'repeat(3, minmax(0, 1fr))', xs: '1fr' }
        }}
      >
        <RenewalPersonalDataCard
          appId={RENEWAL_APP_IDS.personalCard}
          data={data.personal}
          editDisabled={editing !== null}
          editing={editing === 'personal'}
          onClose={close}
          onEdit={() => setEditing('personal')}
          onSave={onSaveCustomer}
        />
        <RenewalAddressCard
          appId={RENEWAL_APP_IDS.addressCard}
          data={data.address}
          editDisabled={editing !== null}
          editing={editing === 'address'}
          onClose={close}
          onEdit={() => setEditing('address')}
          onSave={onSaveAddress}
        />
        <RenewalEquipmentCard appId={RENEWAL_APP_IDS.equipmentCard} data={data.equipment} />
      </Box>

      {offer ? <RenewalOfferSection {...offer} editingCard={editing !== null} /> : null}
    </Box>
  );
}
